
import { Conditions, Expression, FilterExpression, LogicalExpression, Operators } from '@liminil/data-filter';
import { RuleGroupArray, RuleGroupType, RuleType, Field } from 'react-querybuilder'
import { createUUID, isFloat, isInteger } from '../../common';

type ScopedFilterExpression = FilterExpression & { scope?: string };


/**
 * Converts a react-querybuilder rule group into a logical expression tree.
 * @param query Rule group produced by the query builder UI.
 * @returns LogicalExpression representing the provided query structure.
 */
export const queryToExpression = (query: RuleGroupType, fields: Field[], scope?: string) => {

    let logEx: LogicalExpression = {
        condition: query.combinator as Conditions,
        expressions: internalQueryToEx(query.rules, fields, scope)

    }
    return logEx;
}

/**
 * Recursively maps rule arrays into data-filter expression nodes.
 * @param rules Set of rules or nested groups from the query builder.
 * @returns Array of expression nodes.
 */
const internalQueryToEx = (rules: RuleGroupArray, fields: Field[],scope?: string) => {

    let expressions = new Array<Expression>();

    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];
        if (rule.hasOwnProperty("combinator")) {
            const grpRule = rule as RuleGroupType;
            let exp: LogicalExpression = {
                condition: grpRule.combinator as Conditions,
                expressions: internalQueryToEx(grpRule.rules, fields, scope)
            }
            expressions.push(exp)
        } else {
            const filterRule = rule as RuleType
            let value = filterRule.value;
            let field = fields.find(f => f.name === filterRule.field)
            if (field?.inputType === "number" && isFloat(value))
                value = parseFloat(value)
            else if (field?.inputType === "number" && isInteger(value))
                value = parseInt(value)
            let exp: ScopedFilterExpression = {
                id: filterRule.id ?? createUUID(),
                field: filterRule.field,
                operator: filterRule.operator as Operators,
                value: value,
                scope: scope
            }
            expressions.push(exp)
        }
    }
    return expressions;
}

/**
 * Converts a logical expression tree back into react-querybuilder rule groups.
 * @param expression Root logical expression.
 * @returns RuleGroupType compatible with the query builder component.
 */
export const expressionToQuery = (expression: LogicalExpression) => {

    let query: RuleGroupType = {
        combinator: expression.condition,
        rules: internalExToQuery(expression.expressions)
    }
    return query;
}

/**
 * Recursively translates logical/leaf expressions into rule groups and rules.
 * @param expressions LogicalExpression or FilterExpression nodes to convert.
 * @returns RuleGroupArray for react-querybuilder.
 */
const internalExToQuery = (expressions: Expression[]) => {
    let rules: RuleGroupArray = []
    for (let i = 0; i < expressions.length; i++) {
        const expression = expressions[i];
        if (expression.hasOwnProperty("condition")) {
            let logEx = expression as LogicalExpression;
            let ruleGroup: RuleGroupType = {
                id: createUUID(),
                combinator: logEx.condition,
                rules: internalExToQuery(logEx.expressions)
            }
            rules.push(ruleGroup)

        } else {
            const filterEx = expression as FilterExpression;
            let rule: RuleType = {
                id: filterEx.id?.toString() ?? createUUID(),
                field: filterEx.field,
                operator: filterEx.operator,
                value: filterEx.value,
            }
            rules.push(rule)
        }
    }
    return rules;
}
